import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Send } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

import { useGetCurrentUser } from '@/api/auth/auth';
import {
  classifyFlowgateResultError,
  useFlowgateDiscussion,
  usePostFlowgateDiscussion,
  type FlowgateDiscussionEntry,
} from '@/api/flowgate';

const dayLabel = (iso: string) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const sameRef = (
  left: string | number | null | undefined,
  right: string | number | null | undefined,
) =>
  left != null &&
  left !== '' &&
  right != null &&
  right !== '' &&
  String(left) === String(right);

const variantLabelFor = (
  entry: FlowgateDiscussionEntry,
  entries: FlowgateDiscussionEntry[],
) => {
  if (entry.variant_label) return entry.variant_label;
  const sibling = entries.find(
    (other) =>
      !!other.variant_label &&
      other.id !== entry.id &&
      (sameRef(entry.selection_id, other.selection_id) ||
        sameRef(entry.row_id, other.row_id)),
  );
  return sibling?.variant_label ?? '';
};

const eventSentence = (entry: FlowgateDiscussionEntry) => {
  const action =
    entry.kind === 'variant_rejected'
      ? 'rejected'
      : entry.kind === 'variant_comment'
        ? 'commented on'
        : 'added';
  const detail = entry.variant_label ? ` ${entry.variant_label}` : '';
  return `${entry.author_name} ${action} a variant${detail}`;
};

const MessageBubble = ({
  entry,
  mine,
}: {
  entry: FlowgateDiscussionEntry;
  mine: boolean;
}) => (
  <Stack
    alignItems={mine ? 'flex-end' : 'flex-start'}
    sx={{ maxWidth: '100%' }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
      {mine ? 'You' : entry.author_name} · {timeLabel(entry.created_at)}
    </Typography>
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: '70%' },
        px: 1.5,
        py: 1,
        borderRadius: 2,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        ...(mine
          ? { bgcolor: 'primary.main', color: 'primary.contrastText' }
          : {
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }),
      }}
    >
      <Typography variant="body2" sx={{ color: 'inherit' }}>
        {entry.body}
      </Typography>
    </Box>
  </Stack>
);

const SystemEvent = ({ entry }: { entry: FlowgateDiscussionEntry }) => {
  const rejected = entry.kind === 'variant_rejected';
  const initial = (entry.author_name || '?').charAt(0).toUpperCase();
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: rejected ? 'error.light' : 'primary.light',
          color: rejected ? 'error.dark' : 'primary.dark',
        }}
      >
        {initial}
      </Avatar>
      <Box sx={{ minWidth: 0, pt: 0.25 }}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 700 }}>
            {eventSentence(entry)}
          </Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {' '}
            · {timeLabel(entry.created_at)}
          </Box>
        </Typography>
        {(rejected || entry.kind === 'variant_comment') && entry.body && (
          <Box
            sx={{
              mt: 0.5,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.5,
              ...(rejected
                ? {
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                    border: '1px solid',
                    borderColor: (theme) =>
                      alpha(theme.palette.error.main, 0.24),
                  }
                : {
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }),
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {entry.body}
            </Typography>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

const FlowgateDiscussion = ({ resultId }: { resultId: string }) => {
  const { data: currentUser } = useGetCurrentUser();
  const {
    data: entries = [],
    isLoading,
    error,
    refetch,
  } = useFlowgateDiscussion(resultId);
  const postMessage = usePostFlowgateDiscussion();
  const [draft, setDraft] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  useEffect(() => {
    if (!stickToBottom.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [entries]);

  const send = () => {
    const body = draft.trim();
    if (!body || postMessage.isPending) return;
    stickToBottom.current = true;
    postMessage.mutate(
      { resultId, body },
      {
        onSuccess: () => setDraft(''),
        onError: (err) => {
          const info = classifyFlowgateResultError(err);
          enqueueSnackbar(info.message, { variant: 'error' });
        },
      },
    );
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  let previousDay = '';

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="h2">Discussion</Typography>
        <Typography variant="body2" color="text.secondary">
          Notes on this result, plus who added, commented on, or rejected each
          variant.
          Everyone who can open the result sees this thread.
        </Typography>
      </Box>

      <Box
        ref={scrollerRef}
        onScroll={() => {
          const el = scrollerRef.current;
          if (!el) return;
          const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
          stickToBottom.current = distance < 80;
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: 2,
          py: 2,
          bgcolor: 'background.default',
        }}
      >
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {classifyFlowgateResultError(error).message}
          </Alert>
        )}
        {!isLoading && !error && entries.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 650 }}>
              No discussion yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Add a variant on the workbench, or leave a note below.
            </Typography>
          </Box>
        )}
        <Stack spacing={1.25}>
          {entries.map((entry) => {
            const day = dayLabel(entry.created_at);
            const showDay = day !== previousDay;
            previousDay = day;
            const mine =
              !!currentUser?.email &&
              entry.author_email.toLowerCase() ===
                currentUser.email.toLowerCase();
            return (
              <Box key={entry.id}>
                {showDay && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      my: 1,
                      fontWeight: 650,
                    }}
                  >
                    {day}
                  </Typography>
                )}
                {entry.kind === 'message' ? (
                  <MessageBubble entry={entry} mine={mine} />
                ) : (
                  <SystemEvent
                    entry={{
                      ...entry,
                      variant_label: variantLabelFor(entry, entries),
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-end"
        sx={{
          p: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Write a note for the team"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onComposerKeyDown}
          inputProps={{ maxLength: 2000, 'aria-label': 'Discussion message' }}
        />
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={send}
          disabled={!draft.trim() || postMessage.isPending}
          sx={{ flexShrink: 0 }}
        >
          Send
        </Button>
      </Stack>
    </Box>
  );
};

export default FlowgateDiscussion;
